import React, {useCallback, useEffect, useState} from 'react';
import {View, ScrollView, StyleSheet, TouchableOpacity, Switch} from 'react-native';
import {
  Text, TextInput, Button, Appbar, Snackbar, Chip, Dialog, Portal, Divider, IconButton,
} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useNavigation, useRoute} from '@react-navigation/native';
import {useForm, Controller} from 'react-hook-form';
import * as yup from 'yup';
import {yupResolver} from '@hookform/resolvers/yup';
import {userApi} from '../../services/api/userApi';
import {orgUnitApi} from '../../services/api/orgUnitApi';
import {roleApi} from '../../services/api/roleApi';
import {useAuthStore} from '../../store/useAuthStore';
import PhoneInput from '../../components/common/PhoneInput';
import {colors, spacing} from '../../theme';

const UserFormScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const route = useRoute();
  const userId = route.params?.userId;
  const isEditing = !!userId;

  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState('');
  const tenantCountryCode = useAuthStore(s => s.user?.tenant?.countryCode) || '+91';
  const [countryCode, setCountryCode] = useState(tenantCountryCode);

  // Lookup data
  const [orgUnits, setOrgUnits] = useState([]);
  const [roles, setRoles] = useState([]);

  // Org-role assignment state
  const [orgRoleRows, setOrgRoleRows] = useState([]);
  const [sameRoleForAll, setSameRoleForAll] = useState(true);
  const [sharedRoleId, setSharedRoleId] = useState(null);

  // Dialogs
  const [orgUnitDialogVisible, setOrgUnitDialogVisible] = useState(false);
  const [roleDialogVisible, setRoleDialogVisible] = useState(false);
  const [roleDialogTarget, setRoleDialogTarget] = useState(null); // null = shared, index = per-row

  const schema = yup.object().shape({
    fullName: yup.string().required(t('validation.required')),
    phone: yup.string().required(t('validation.required')),
    email: yup.string().email(t('validation.email')).nullable(),
    password: isEditing
      ? yup.string().nullable()
      : yup.string().required(t('validation.required')),
    notes: yup.string().nullable(),
  });

  const {control, handleSubmit, reset, formState: {errors}} = useForm({
    resolver: yupResolver(schema),
    defaultValues: {fullName: '', phone: '', email: '', password: '', notes: ''},
  });

  useEffect(() => {
    const loadLookups = async () => {
      try {
        const [ouRes, roleRes] = await Promise.all([
          orgUnitApi.listSimple(),
          roleApi.list(),
        ]);
        const ous = ouRes.data.data || [];
        setOrgUnits(ous);
        setRoles(roleRes.data.data || []);

        // Auto-assign if only one org unit
        if (ous.length === 1 && !isEditing) {
          setOrgRoleRows([{orgUnitId: ous[0].id, orgUnitName: ous[0].name, roleId: null, roleName: null, isPrimary: true}]);
        }
      } catch (e) {
        // silent
      }
    };
    loadLookups();
  }, [isEditing]);

  useEffect(() => {
    if (isEditing) {
      const loadData = async () => {
        try {
          const res = await userApi.getById(userId);
          const u = res.data.data;
          if (u.countryCode) setCountryCode(u.countryCode);
          reset({
            fullName: u.fullName || '',
            phone: u.phone || '',
            email: u.email || '',
            password: '',
            notes: u.notes || '',
          });
          if (u.orgRoleAssignments && u.orgRoleAssignments.length > 0) {
            const rows = u.orgRoleAssignments.map(a => ({
              orgUnitId: a.orgUnitId,
              orgUnitName: a.orgUnitName,
              roleId: a.roleId,
              roleName: a.roleName,
              isPrimary: a.isPrimary,
            }));
            setOrgRoleRows(rows);
            // Check if all roles are the same
            const uniqueRoles = [...new Set(rows.map(r => r.roleId))];
            if (uniqueRoles.length === 1 && uniqueRoles[0]) {
              setSameRoleForAll(true);
              setSharedRoleId(uniqueRoles[0]);
            } else {
              setSameRoleForAll(false);
            }
          }
        } catch (e) {
          setSnackbar(e.response?.data?.message || t('common.error'));
        }
      };
      loadData();
    }
  }, [isEditing, userId, reset, t]);

  // When sameRoleForAll + sharedRoleId changes, sync all rows
  useEffect(() => {
    if (sameRoleForAll && sharedRoleId) {
      const roleName = roles.find(r => r.id === sharedRoleId)?.name || null;
      setOrgRoleRows(prev => prev.map(row => ({...row, roleId: sharedRoleId, roleName})));
    }
  }, [sameRoleForAll, sharedRoleId, roles]);

  // Auto-primary when only one org
  useEffect(() => {
    if (orgRoleRows.length === 1 && !orgRoleRows[0].isPrimary) {
      setOrgRoleRows([{...orgRoleRows[0], isPrimary: true}]);
    }
  }, [orgRoleRows.length]);

  const toggleOrgUnit = useCallback((orgId) => {
    const ou = orgUnits.find(o => o.id === orgId);
    setOrgRoleRows(prev => {
      const exists = prev.find(r => r.orgUnitId === orgId);
      if (exists) {
        return prev.filter(r => r.orgUnitId !== orgId);
      }
      const roleId = sameRoleForAll ? sharedRoleId : null;
      const roleName = sameRoleForAll && sharedRoleId ? (roles.find(r => r.id === sharedRoleId)?.name || null) : null;
      return [...prev, {orgUnitId: orgId, orgUnitName: ou?.name, roleId, roleName, isPrimary: false}];
    });
  }, [orgUnits, sameRoleForAll, sharedRoleId, roles]);

  const setPrimary = useCallback((orgId) => {
    setOrgRoleRows(prev => prev.map(r => ({...r, isPrimary: r.orgUnitId === orgId})));
  }, []);

  const setRowRole = useCallback((index, roleId) => {
    const roleName = roles.find(r => r.id === roleId)?.name || null;
    setOrgRoleRows(prev => prev.map((r, i) => i === index ? {...r, roleId, roleName} : r));
  }, [roles]);

  const removeRow = useCallback((index) => {
    setOrgRoleRows(prev => prev.filter((_, i) => i !== index));
  }, []);

  const onSubmit = useCallback(async (data, addAnother = false) => {
    // Validate org-role assignments
    if (orgRoleRows.length === 0) {
      setSnackbar(t('user.orgRoleRequired'));
      return;
    }
    const missingRole = orgRoleRows.find(r => !r.roleId);
    if (missingRole) {
      setSnackbar(t('user.everyOrgNeedsRole'));
      return;
    }
    const hasPrimary = orgRoleRows.some(r => r.isPrimary);
    if (!hasPrimary) {
      setSnackbar(t('user.primaryRequired'));
      return;
    }

    setSaving(true);
    try {
      const payload = {
        fullName: data.fullName,
        countryCode,
        phone: data.phone,
        email: data.email || null,
        notes: data.notes || null,
        orgRoleAssignments: orgRoleRows.map(r => ({
          orgUnitId: r.orgUnitId,
          roleId: r.roleId,
          isPrimary: r.isPrimary,
        })),
      };
      if (!isEditing) {
        payload.password = data.password;
      }

      if (isEditing) {
        await userApi.update(userId, payload);
      } else {
        await userApi.create(payload);
      }

      if (addAnother) {
        reset({fullName: '', phone: '', email: '', password: '', notes: ''});
        setOrgRoleRows([]);
        setSharedRoleId(null);
        setSnackbar(t('user.savedAddAnother'));
      } else {
        navigation.goBack();
      }
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    } finally {
      setSaving(false);
    }
  }, [isEditing, userId, navigation, t, orgRoleRows, reset]);

  const selectedOrgIds = orgRoleRows.map(r => r.orgUnitId);
  const sharedRoleName = roles.find(r => r.id === sharedRoleId)?.name || '';

  return (
    <View style={styles.container}>
      <Appbar.Header style={styles.appbar} dark>
        <Appbar.BackAction onPress={() => navigation.goBack()} color={colors.white} />
        <Appbar.Content
          title={isEditing ? t('user.editUser') : t('user.addUser')}
          titleStyle={styles.appbarTitle}
        />
      </Appbar.Header>

      <ScrollView style={styles.form} keyboardShouldPersistTaps="handled">
        {/* Section 1: Basic Information */}
        <Text variant="titleSmall" style={styles.sectionTitle}>{t('user.sectionBasic')}</Text>
        <Divider style={styles.divider} />

        <Controller control={control} name="fullName" render={({field: {onChange, onBlur, value}}) => (
          <TextInput label={`${t('user.fullName')} *`} value={value} onChangeText={onChange} onBlur={onBlur}
            error={!!errors.fullName} mode="outlined" style={styles.input}
            outlineColor={colors.border} activeOutlineColor={colors.primary} />
        )} />
        <Controller control={control} name="phone" render={({field: {onChange, value}}) => (
          <PhoneInput
            countryCode={countryCode}
            onCountryCodeChange={setCountryCode}
            phone={value}
            onPhoneChange={onChange}
            label={`${t('user.phone')} *`}
            error={!!errors.phone}
          />
        )} />
        <Controller control={control} name="email" render={({field: {onChange, onBlur, value}}) => (
          <TextInput label={t('user.email')} value={value} onChangeText={onChange} onBlur={onBlur}
            error={!!errors.email} mode="outlined" style={styles.input} keyboardType="email-address"
            autoCapitalize="none"
            outlineColor={colors.border} activeOutlineColor={colors.primary} />
        )} />
        {!isEditing && (
          <Controller control={control} name="password" render={({field: {onChange, onBlur, value}}) => (
            <TextInput label={`${t('user.password')} *`} value={value} onChangeText={onChange} onBlur={onBlur}
              error={!!errors.password} mode="outlined" style={styles.input} secureTextEntry
              outlineColor={colors.border} activeOutlineColor={colors.primary} />
          )} />
        )}

        {/* Section 2: Store & Role Assignment */}
        <Text variant="titleSmall" style={[styles.sectionTitle, {marginTop: spacing.lg}]}>{t('user.sectionOrgRole')}</Text>
        <Divider style={styles.divider} />

        {/* Assigned Stores selector */}
        <TouchableOpacity onPress={() => setOrgUnitDialogVisible(true)}>
          <TextInput
            label={`${t('user.assignedStores')} *`}
            value={orgRoleRows.map(r => r.orgUnitName).join(', ')}
            mode="outlined"
            style={styles.input}
            editable={false}
            right={<TextInput.Icon icon="chevron-down" onPress={() => setOrgUnitDialogVisible(true)} />}
            outlineColor={colors.border}
          />
        </TouchableOpacity>

        {orgRoleRows.length > 0 && (
          <>
            {/* Same role toggle */}
            <View style={styles.toggleRow}>
              <Text variant="bodyMedium" style={styles.toggleLabel}>{t('user.sameRoleForAll')}</Text>
              <Switch
                value={sameRoleForAll}
                onValueChange={setSameRoleForAll}
                trackColor={{true: colors.primary + '80'}}
                thumbColor={sameRoleForAll ? colors.primary : colors.textLight}
              />
            </View>

            {sameRoleForAll ? (
              /* Single shared role selector */
              <TouchableOpacity onPress={() => { setRoleDialogTarget(null); setRoleDialogVisible(true); }}>
                <TextInput
                  label={`${t('user.role')} *`}
                  value={sharedRoleName}
                  mode="outlined"
                  style={styles.input}
                  editable={false}
                  right={<TextInput.Icon icon="chevron-down" onPress={() => { setRoleDialogTarget(null); setRoleDialogVisible(true); }} />}
                  outlineColor={colors.border}
                />
              </TouchableOpacity>
            ) : (
              /* Per-row role selectors */
              orgRoleRows.map((row, index) => (
                <View key={row.orgUnitId} style={styles.orgRoleRow}>
                  <View style={styles.orgRoleInfo}>
                    <Text variant="bodyMedium" style={styles.orgRoleStoreName}>{row.orgUnitName}</Text>
                    <TouchableOpacity onPress={() => { setRoleDialogTarget(index); setRoleDialogVisible(true); }}>
                      <View style={styles.roleSelector}>
                        <Text style={[styles.roleSelectorText, !row.roleName && {color: colors.textLight}]}>
                          {row.roleName || t('user.selectRole')}
                        </Text>
                        <IconButton icon="chevron-down" size={16} style={{margin: 0, padding: 0}} />
                      </View>
                    </TouchableOpacity>
                  </View>
                  <View style={styles.orgRoleActions}>
                    <TouchableOpacity onPress={() => setPrimary(row.orgUnitId)}>
                      <View style={[styles.radioOuter, row.isPrimary && styles.radioOuterSelected]}>
                        {row.isPrimary && <View style={styles.radioInner} />}
                      </View>
                    </TouchableOpacity>
                    <IconButton
                      icon="close"
                      size={18}
                      iconColor={colors.error}
                      onPress={() => removeRow(index)}
                      style={styles.removeBtn}
                    />
                  </View>
                </View>
              ))
            )}

            {/* Primary selector when sameRoleForAll and multiple orgs */}
            {sameRoleForAll && orgRoleRows.length > 1 && (
              <View style={styles.primarySection}>
                <Text variant="bodySmall" style={styles.primaryLabel}>{t('user.primaryStore')}</Text>
                <View style={styles.primaryChipRow}>
                  {orgRoleRows.map(row => (
                    <Chip
                      key={row.orgUnitId}
                      selected={row.isPrimary}
                      onPress={() => setPrimary(row.orgUnitId)}
                      style={styles.primaryChip}
                      textStyle={{fontSize: 13}}>
                      {row.orgUnitName}
                    </Chip>
                  ))}
                </View>
              </View>
            )}
          </>
        )}

        {/* Section 3: Notes */}
        <Text variant="titleSmall" style={[styles.sectionTitle, {marginTop: spacing.lg}]}>{t('user.sectionNotes')}</Text>
        <Divider style={styles.divider} />

        <Controller control={control} name="notes" render={({field: {onChange, onBlur, value}}) => (
          <TextInput label={t('user.notes')} value={value} onChangeText={onChange} onBlur={onBlur}
            mode="outlined" style={[styles.input, {height: 80}]} multiline numberOfLines={3}
            outlineColor={colors.border} activeOutlineColor={colors.primary} />
        )} />

        {/* Validation errors */}
        {Object.keys(errors).length > 0 && (
          <Text style={styles.errorText}>{errors[Object.keys(errors)[0]]?.message}</Text>
        )}

        {/* Actions */}
        <View style={styles.buttonRow}>
          <Button mode="contained" onPress={handleSubmit(data => onSubmit(data, false))} loading={saving} disabled={saving}
            style={styles.saveButton} buttonColor={colors.primary}>
            {t('common.save')}
          </Button>
          {!isEditing && (
            <Button mode="outlined" onPress={handleSubmit(data => onSubmit(data, true))} disabled={saving}
              style={styles.saveAnotherButton} textColor={colors.primary}>
              {t('user.saveAndAddAnother')}
            </Button>
          )}
        </View>
        <View style={{height: 40}} />
      </ScrollView>

      {/* OrgUnit Multi-Select Dialog */}
      <Portal>
        <Dialog visible={orgUnitDialogVisible} onDismiss={() => setOrgUnitDialogVisible(false)} style={{borderRadius: 15}}>
          <Dialog.Title>{t('user.selectStores')}</Dialog.Title>
          <Dialog.ScrollArea style={{maxHeight: 300, paddingHorizontal: 0}}>
            <View style={styles.dialogChipGroup}>
              {orgUnits.map(ou => (
                <Chip
                  key={ou.id}
                  selected={selectedOrgIds.includes(ou.id)}
                  onPress={() => toggleOrgUnit(ou.id)}
                  style={styles.dialogChip}
                  textStyle={{fontSize: 14}}>
                  {ou.name}
                </Chip>
              ))}
            </View>
          </Dialog.ScrollArea>
          <Dialog.Actions>
            <Button onPress={() => setOrgUnitDialogVisible(false)}>{t('common.done')}</Button>
          </Dialog.Actions>
        </Dialog>

        {/* Role Selector Dialog */}
        <Dialog visible={roleDialogVisible} onDismiss={() => setRoleDialogVisible(false)} style={{borderRadius: 15}}>
          <Dialog.Title>{t('user.selectRole')}</Dialog.Title>
          <Dialog.ScrollArea style={{maxHeight: 300, paddingHorizontal: 0}}>
            <View style={styles.dialogChipGroup}>
              {roles.map(r => (
                <Chip
                  key={r.id}
                  selected={roleDialogTarget === null ? sharedRoleId === r.id : orgRoleRows[roleDialogTarget]?.roleId === r.id}
                  onPress={() => {
                    if (roleDialogTarget === null) {
                      setSharedRoleId(r.id);
                    } else {
                      setRowRole(roleDialogTarget, r.id);
                    }
                    setRoleDialogVisible(false);
                  }}
                  style={styles.dialogChip}
                  textStyle={{fontSize: 14}}>
                  {r.name}
                </Chip>
              ))}
            </View>
          </Dialog.ScrollArea>
          <Dialog.Actions>
            <Button onPress={() => setRoleDialogVisible(false)}>{t('common.cancel')}</Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>

      <Snackbar visible={!!snackbar} onDismiss={() => setSnackbar('')} duration={3000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  appbar: {backgroundColor: colors.primary},
  appbarTitle: {color: colors.white},
  form: {padding: spacing.md},
  sectionTitle: {fontWeight: '700', color: colors.primary, marginTop: spacing.sm, marginBottom: spacing.xs},
  divider: {marginBottom: spacing.sm},
  input: {marginBottom: spacing.sm, backgroundColor: colors.surface},
  errorText: {color: colors.error, marginBottom: spacing.sm, fontSize: 12},
  buttonRow: {marginTop: spacing.lg, gap: spacing.sm},
  saveButton: {borderRadius: 8},
  saveAnotherButton: {borderRadius: 8, borderColor: colors.primary},
  toggleRow: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingVertical: spacing.xs, marginBottom: spacing.sm,
  },
  toggleLabel: {color: colors.text, fontWeight: '500'},
  orgRoleRow: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    backgroundColor: colors.surface, borderRadius: 8, padding: spacing.sm,
    marginBottom: spacing.xs, borderWidth: 1, borderColor: colors.border,
  },
  orgRoleInfo: {flex: 1},
  orgRoleStoreName: {fontWeight: '600', color: colors.text, marginBottom: 4},
  roleSelector: {
    flexDirection: 'row', alignItems: 'center',
    backgroundColor: colors.background, borderRadius: 6, paddingHorizontal: 8, paddingVertical: 4,
  },
  roleSelectorText: {fontSize: 13, color: colors.text, flex: 1},
  orgRoleActions: {flexDirection: 'row', alignItems: 'center', gap: spacing.xs},
  radioOuter: {
    width: 20, height: 20, borderRadius: 10, borderWidth: 2,
    borderColor: colors.textLight, justifyContent: 'center', alignItems: 'center',
  },
  radioOuterSelected: {borderColor: colors.primary},
  radioInner: {width: 10, height: 10, borderRadius: 5, backgroundColor: colors.primary},
  removeBtn: {margin: 0, padding: 0, width: 28, height: 28},
  primarySection: {marginBottom: spacing.sm},
  primaryLabel: {color: colors.textSecondary, marginBottom: spacing.xs, fontWeight: '500'},
  primaryChipRow: {flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs},
  primaryChip: {backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border},
  dialogChipGroup: {flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm, padding: spacing.lg},
  dialogChip: {backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border},
});

export default UserFormScreen;
